import type {
  DashboardStatus,
  Portfolio,
  Position,
  Signal,
  Strategy,
  RiskParams,
  RiskExposure,
  WatchlistItem,
  PerformanceData,
  AlertConfig,
  DrawdownStatus,
  AgentStatus,
  PaperTrade,
  PaperStats,
  BucketsResponse,
  PairStats,
  ClosedTrade,
  CoreSatelliteStatus,
  MoonshotSummary,
  CFMBalance,
  AcesStatus,
  AcesFinding,
  AcesFix,
  AcesBudget,
  AcesAnomaly,
  StrategyTunerReport,
  AriaStatus,
  DayEngineStatus,
  DayEngineTrade,
  DayEngineSummary,
  DayEngineGates,
  DayEngineDecisions,
  AlpacaPosition,
  AlpacaOrder,
  AlpacaAccount,
  CoinbaseEquitiesSummary,
  CoinbaseEquityPosition,
  CoinbaseEquityOrder,
  CoinbaseEquityProduct,
  LeversResponse,
  LeverProposal,
} from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '';

// WebAuthn types
interface PublicKeyCredentialCreationOptionsJSON {
  rp: { name: string; id: string };
  user: { id: string; name: string; displayName: string };
  challenge: string;
  pubKeyCredParams: Array<{ type: string; alg: number }>;
  timeout?: number;
  excludeCredentials?: Array<{ type: string; id: string; transports?: string[] }>;
  authenticatorSelection?: {
    authenticatorAttachment?: string;
    residentKey?: string;
    requireResidentKey?: boolean;
    userVerification?: string;
  };
  attestation?: string;
  challengeId: string;
}

interface PublicKeyCredentialRequestOptionsJSON {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: Array<{ type: string; id: string; transports?: string[] }>;
  userVerification?: string;
  challengeId: string;
}

interface AuthResponse {
  status: string;
  message: string;
  user: {
    id: string;
    username: string;
    display_name: string;
    is_admin?: boolean;
  };
  tokens: {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
  };
}

interface Passkey {
  id: string;
  device_name: string | null;
  device_type: string | null;
  created_at: string;
  last_used: string | null;
}

class ApiService {
  private headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  constructor() {
    // Load token from localStorage on initialization
    this.initTokenFromStorage();
  }

  private initTokenFromStorage() {
    try {
      const stored = localStorage.getItem('tradedesk-auth');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.state?.accessToken) {
          this.setAuthToken(parsed.state.accessToken);
        }
      }
    } catch {
      // Ignore parse errors
    }
  }

  setAuthToken(token: string | null) {
    if (token) {
      this.headers = {
        ...this.headers,
        'Authorization': `Bearer ${token}`,
      };
    } else {
      const { Authorization: _, ...rest } = this.headers as Record<string, string>;
      this.headers = rest;
    }
  }

  private async fetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: { ...this.headers, ...options?.headers },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Status
  async getStatus(): Promise<DashboardStatus> {
    return this.fetch('/api/status');
  }

  async getHealth(): Promise<{ status: string; bot_running: boolean; timestamp: string }> {
    return this.fetch('/api/health');
  }

  async getPipelineHealth(): Promise<{
    healthy: boolean;
    status: string;
    checks: Array<{
      name: string;
      passed: boolean;
      message: string;
      severity: string;
    }>;
    timestamp: string;
    error?: string;
  }> {
    return this.fetch('/api/pipeline-health');
  }

  // Portfolio
  async getPortfolio(): Promise<Portfolio> {
    return this.fetch('/api/portfolio');
  }

  // Positions
  async getPositions(): Promise<Position[]> {
    return this.fetch('/api/positions');
  }

  async closePosition(symbol: string, reauthToken?: string): Promise<{ status: string; command_id: string }> {
    const headers: Record<string, string> = {};
    if (reauthToken) {
      headers['X-Reauth-Token'] = reauthToken;
    }
    return this.fetch(`/api/positions/${symbol.replace('/', '-')}/close`, {
      method: 'POST',
      headers,
    });
  }

  async updatePosition(
    symbol: string,
    stopLoss?: number,
    takeProfit?: number
  ): Promise<{ status: string; command_id: string }> {
    const params = new URLSearchParams();
    if (stopLoss) params.append('stop_loss', stopLoss.toString());
    if (takeProfit) params.append('take_profit', takeProfit.toString());

    return this.fetch(`/api/positions/${symbol.replace('/', '-')}?${params}`, {
      method: 'PATCH',
    });
  }

  async partialClose(symbol: string, percent: number, reauthToken?: string): Promise<{ status: string; command_id: string }> {
    const headers: Record<string, string> = {};
    if (reauthToken) {
      headers['X-Reauth-Token'] = reauthToken;
    }
    return this.fetch(`/api/positions/${symbol.replace('/', '-')}/partial?percent=${percent}`, {
      method: 'POST',
      headers,
    });
  }

  async getCommandStatus(commandId: string): Promise<{ id: string; status: string; result?: unknown; error?: string }> {
    return this.fetch(`/api/commands/${commandId}`);
  }

  // Signals
  async getSignals(params?: {
    limit?: number;
    strategy?: string;
    symbol?: string;
    executed_only?: boolean;
  }): Promise<Signal[]> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.strategy) searchParams.append('strategy', params.strategy);
    if (params?.symbol) searchParams.append('symbol', params.symbol);
    if (params?.executed_only) searchParams.append('executed_only', 'true');

    return this.fetch(`/api/signals?${searchParams}`);
  }

  // Strategies
  async getStrategies(): Promise<Strategy[]> {
    return this.fetch('/api/strategies');
  }

  async updateStrategyParams(name: string, params: {
    min_confidence?: number;
    trend_strength_threshold?: number;
    rsi_oversold?: number;
    rsi_overbought?: number;
    volume_ratio_min?: number;
  }): Promise<{ strategy: string; params: unknown }> {
    return this.fetch(`/api/strategies/${name}/params`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async toggleStrategy(name: string, enabled: boolean): Promise<{ strategy: string; enabled: boolean }> {
    return this.fetch(`/api/strategies/${name}/toggle?enabled=${enabled}`, {
      method: 'POST',
    });
  }

  // Pair Stats (Symbol-level Win/Loss)
  async getPairStats(): Promise<PairStats[]> {
    return this.fetch('/api/pair-stats');
  }

  async getClosedTrades(days: number = 7, limit: number = 30): Promise<ClosedTrade[]> {
    return this.fetch(`/api/closed-trades?days=${days}&limit=${limit}`);
  }

  // Watchlist
  async getWatchlist(): Promise<WatchlistItem[]> {
    return this.fetch('/api/watchlist');
  }

  async updateWatchlist(symbols: string[]): Promise<{ watchlist: string[] }> {
    return this.fetch('/api/watchlist', {
      method: 'PATCH',
      body: JSON.stringify(symbols),
    });
  }

  async refreshWatchlist(): Promise<{ status: string }> {
    return this.fetch('/api/watchlist/refresh', { method: 'POST' });
  }

  // Risk
  async getRisk(): Promise<{ params: RiskParams; current_exposure: RiskExposure }> {
    return this.fetch('/api/risk');
  }

  async updateRiskParams(params: RiskParams): Promise<{ params: RiskParams }> {
    return this.fetch('/api/risk', {
      method: 'PATCH',
      body: JSON.stringify(params),
    });
  }

  async applyRiskConfig(): Promise<{ applied: boolean; validated: boolean; message: string }> {
    return this.fetch('/api/risk/apply', {
      method: 'POST',
    });
  }

  // Drawdown Status (detailed drawdown tracking)
  async getDrawdownStatus(): Promise<DrawdownStatus> {
    return this.fetch('/api/drawdown');
  }

  // Market Sentiment (Funding + OI + Fear & Greed aggregator)
  async getSentiment(): Promise<any> {
    return this.fetch('/api/sentiment');
  }

  // Macro Intelligence (Causal Reasoning Engine)
  async getMacro(): Promise<any> {
    return this.fetch('/api/macro');
  }

  // Unified Intelligence Engine (Observer + Allocator Brain)
  async getIntelligence(): Promise<any> {
    return this.fetch('/api/intelligence');
  }

  // Strategy Health (Live vs Backtest Comparison)
  async getStrategyHealth(): Promise<any> {
    return this.fetch('/api/strategy-health');
  }

  // CFM Perpetual Futures Balance
  async getCFMBalance(): Promise<CFMBalance> {
    return this.fetch('/api/cfm/balance');
  }

  // Buckets - MAJORS/MIDCAP allocation tracking
  async getBuckets(): Promise<BucketsResponse> {
    return this.fetch('/api/buckets');
  }

  // Core-Satellite System
  async getCoreSatelliteStatus(): Promise<CoreSatelliteStatus> {
    return this.fetch('/api/core-satellite/status');
  }

  async getMoonshotSummary(): Promise<MoonshotSummary> {
    return this.fetch('/api/core-satellite/moonshot');
  }

  async triggerMoonshotScan(): Promise<{
    candidates: Array<{
      symbol: string;
      coinbase_symbol: string;
      name: string;
      rank: number;
      market_cap: number;
      volume_24h: number;
      price: number;
      change_24h: number;
      change_7d: number;
      score: number;
    }>;
    filters: Record<string, number>;
  }> {
    return this.fetch('/api/core-satellite/moonshot/scan', { method: 'POST' });
  }

  // Agent Status
  async getAgentStatus(): Promise<AgentStatus[]> {
    return this.fetch('/api/agents');
  }

  // Performance
  async getPerformance(period: string = '30d'): Promise<PerformanceData> {
    return this.fetch(`/api/performance?period=${period}`);
  }

  // Bot Control
  async emergencyStop(): Promise<{ status: string; bot_killed: boolean; orders_cancelled: number }> {
    return this.fetch('/api/bot/stop', { method: 'POST' });
  }

  async flattenAllPositions(): Promise<{
    status: string;
    positions_closed: number;
    closed: Array<{ symbol: string; quantity: number }>;
    errors: Array<{ symbol: string; error: string }>;
  }> {
    return this.fetch('/api/positions/flatten-all', { method: 'POST' });
  }

  async startBot(mode: 'live' | 'paper' = 'paper'): Promise<{ status: string; mode: string; pid?: number }> {
    return this.fetch(`/api/bot/start?mode=${mode}`, { method: 'POST' });
  }

  async pauseBot(): Promise<{ status: string }> {
    return this.fetch('/api/bot/pause', { method: 'POST' });
  }

  async resumeBot(): Promise<{ status: string }> {
    return this.fetch('/api/bot/resume', { method: 'POST' });
  }

  // Config
  async getConfig(): Promise<Record<string, unknown>> {
    return this.fetch('/api/config');
  }

  // Full Config - SINGLE SOURCE OF TRUTH
  async getFullConfig(): Promise<{
    version: string;
    last_updated: string;
    validated: {
      min_confidence_threshold: number;
      momentum_trend_threshold: number;
      stop_loss_atr: number;
      take_profit_atr: number;
    };
    risk: {
      max_position_size_pct: number;
      max_positions: number;
      max_correlated_exposure_pct: number;
      cash_reserve_min_pct: number;
      daily_risk_limit_pct: number;
      stop_loss_atr_multiplier: number;
      take_profit_atr_multiplier: number;
    };
    ui: {
      strategies: Record<string, {
        icon: string;
        display_name: string;
        description: string;
        regime_best: string;
        color: string;
        enabled_by_default: boolean;
        risk_level: string;
      }>;
      regimes: Record<string, {
        icon: string;
        color: string;
        label: string;
        description: string;
      }>;
      drawdown_thresholds: {
        caution: number;
        warning: number;
        critical: number;
        emergency: number;
        halt: number;
      };
      risk_levels: Record<string, {
        color: string;
        max_exposure_pct: number;
        label: string;
      }>;
      formats: Record<string, unknown>;
      alerts: Record<string, unknown>;
      performance_tiers: Record<string, unknown>;
    };
    system: {
      constants_loaded: boolean;
      bot_modules_loaded: boolean;
      auth_enabled: boolean;
    };
  }> {
    return this.fetch('/api/config/full');
  }

  // Alerts
  async getAlertSettings(): Promise<AlertConfig> {
    return this.fetch('/api/alerts/settings');
  }

  async updateAlertSettings(settings: AlertConfig): Promise<AlertConfig> {
    return this.fetch('/api/alerts/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
  }

  // Paper Trading
  async getPaperTrades(): Promise<PaperTrade[]> {
    return this.fetch('/api/paper/trades');
  }

  async getPaperStats(): Promise<PaperStats> {
    return this.fetch('/api/paper/stats');
  }

  async getPaperHealth(): Promise<{
    status: string;
    win_rate_7d: number;
    net_pnl_7d: number;
    trades_7d: number;
    rs_threshold_adjustment: number;
    size_multiplier: number;
    message: string;
  }> {
    return this.fetch('/api/paper/health');
  }

  // ═══════════════════════════════════════════════════════════
  // SYMBOL PERFORMANCE TRACKER (Auto-Ban System)
  // ═══════════════════════════════════════════════════════════

  async getSymbolTrackerStatus(): Promise<{
    total_symbols_tracked: number;
    banned_symbols: number;
    active_symbols: number;
    total_trades_tracked: number;
    total_pnl: string;
    thresholds: {
      min_trades_for_pf_eval: number;
      pf_threshold: number;
      consecutive_loss_threshold: number;
      ban_duration_consecutive_days: number;
      ban_duration_pf_days: number;
      allow_3_loss_compound: boolean;
      min_cumulative_loss_for_3: number;
    };
  }> {
    return this.fetch('/api/symbol-tracker/status');
  }

  async getBannedSymbols(): Promise<{
    banned: Array<{
      symbol: string;
      reason: string;
      banned_at: string;
      expires_at: string;
      ban_type: string;
      total_trades: number;
      win_rate: string;
      profit_factor: string;
      total_pnl: string;
    }>;
    count: number;
  }> {
    return this.fetch('/api/symbol-tracker/banned');
  }

  async getPoorPerformers(): Promise<{
    poor_performers: Array<{
      symbol: string;
      warning_level: 'OK' | 'WARNING' | 'CRITICAL';
      warnings: string[];
      total_trades: number;
      wins: number;
      losses: number;
      consecutive_losses: number;
      win_rate: string;
      profit_factor: string;
      total_pnl: string;
    }>;
    count: number;
  }> {
    return this.fetch('/api/symbol-tracker/poor-performers');
  }

  async getSymbolStats(symbol: string): Promise<{
    symbol: string;
    error?: string;
    stats?: {
      symbol: string;
      total_trades: number;
      wins: number;
      losses: number;
      consecutive_losses: number;
      max_consecutive_losses: number;
      total_pnl: number;
      gross_profit: number;
      gross_loss: number;
      win_rate: number;
      profit_factor: number;
      last_trade_time: string;
      last_trade_pnl: number;
      is_banned: boolean;
      ban_reason: string;
      banned_at: string;
      ban_expires_at: string;
      ban_type: string;
    };
  }> {
    return this.fetch(`/api/symbol-tracker/stats/${encodeURIComponent(symbol)}`);
  }

  async manualBanSymbol(symbol: string, reason?: string, durationDays?: number): Promise<{
    status: string;
    symbol: string;
    reason: string;
    duration_days: number;
  }> {
    const params = new URLSearchParams();
    if (reason) params.append('reason', reason);
    if (durationDays) params.append('duration_days', durationDays.toString());
    return this.fetch(`/api/symbol-tracker/ban/${encodeURIComponent(symbol)}?${params}`, {
      method: 'POST',
    });
  }

  async manualUnbanSymbol(symbol: string): Promise<{
    status: string;
    symbol: string;
    message?: string;
  }> {
    return this.fetch(`/api/symbol-tracker/ban/${encodeURIComponent(symbol)}`, {
      method: 'DELETE',
    });
  }

  async getStaticBlacklist(): Promise<{
    suspended_symbols: string[];
  }> {
    const data = await this.fetch<{suspended_symbols: string[]}>('/api/compliance/status');
    return { suspended_symbols: data.suspended_symbols || [] };
  }

  // ═══════════════════════════════════════════════════════════
  // ALPACA STOCK TRADING
  // ═══════════════════════════════════════════════════════════

  // ISS-090: these three returned INLINE shapes with `side: string` — the
  // fourth copy of the position type, and the one furthest from the render
  // site. They now return the shared types, so `side` is a union everywhere
  // from transport to pixel.
  async getAlpacaAccount(): Promise<AlpacaAccount> {
    return this.fetch('/api/alpaca/account');
  }

  async getAlpacaPositions(): Promise<AlpacaPosition[]> {
    return this.fetch('/api/alpaca/positions');
  }

  async getAlpacaOrders(params?: { status?: string; limit?: number }): Promise<AlpacaOrder[]> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    return this.fetch(`/api/alpaca/orders?${searchParams}`);
  }

  async getAlpacaStats(): Promise<{
    // Truth (from Alpaca)
    equity: number;
    cash: number;
    starting_capital: number;
    total_pnl: number;
    return_pct: number;
    open_positions: number;
    // Attribution (informational)
    total_trades: number;
    winning_trades: number;
    losing_trades: number;
    win_rate: number;
  }> {
    return this.fetch('/api/alpaca/stats');
  }

  // ── Day Engine (intraday long/short — the only Alpaca surface that shorts)
  async getDayEngineStatus(): Promise<DayEngineStatus> {
    return this.fetch('/api/day-engine/status');
  }

  async getDayEngineTrades(params?: {
    side?: 'long' | 'short';
    setup?: string;
    status?: 'OPEN' | 'CLOSED';
    limit?: number;
  }): Promise<{ trades: DayEngineTrade[]; count: number; ledger_available: boolean }> {
    const sp = new URLSearchParams();
    if (params?.side) sp.append('side', params.side);
    if (params?.setup) sp.append('setup', params.setup);
    if (params?.status) sp.append('status', params.status);
    if (params?.limit) sp.append('limit', params.limit.toString());
    return this.fetch(`/api/day-engine/trades?${sp}`);
  }

  async getDayEngineSummary(): Promise<DayEngineSummary> {
    return this.fetch('/api/day-engine/summary');
  }

  async getDayEngineGates(): Promise<DayEngineGates> {
    return this.fetch('/api/day-engine/gates');
  }

  /** ISS-092 — declines. The answer to "why didn't it short that?" */
  async getDayEngineDecisions(params?: {
    side?: 'long' | 'short';
    state?: 'REJECTED' | 'FAILED';
    days?: number;
    limit?: number;
  }): Promise<DayEngineDecisions> {
    const sp = new URLSearchParams();
    if (params?.side) sp.append('side', params.side);
    if (params?.state) sp.append('state', params.state);
    if (params?.days) sp.append('days', params.days.toString());
    if (params?.limit) sp.append('limit', params.limit.toString());
    return this.fetch(`/api/day-engine/decisions?${sp}`);
  }

  // ═══════════════════════════════════════════════════════════
  // COINBASE EQUITIES (US stocks on Coinbase, read-only)
  // ═══════════════════════════════════════════════════════════

  async getCoinbaseEquitiesSummary(): Promise<CoinbaseEquitiesSummary> {
    return this.fetch('/api/coinbase/equities/summary');
  }

  // Owner levers (home page). GETs are read-only; propose writes a PROPOSAL
  // record + Telegram receipt and never edits config or live state.
  async getLevers(): Promise<LeversResponse> {
    return this.fetch('/api/levers');
  }

  async getLeverProposals(limit = 20): Promise<{ proposals: LeverProposal[] }> {
    return this.fetch(`/api/levers/proposals?limit=${limit}`);
  }

  async proposeLever(leverId: string, note: string): Promise<{ id: number; lever: string; title: string; ready: boolean; blocking: string[]; telegram_sent: boolean; created?: boolean; note?: string | null }> {
    return this.fetch(`/api/levers/${encodeURIComponent(leverId)}/propose`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  }

  async getCoinbaseEquitiesPositions(): Promise<CoinbaseEquityPosition[]> {
    return this.fetch('/api/coinbase/equities/positions');
  }

  async getCoinbaseEquitiesOrders(params?: {
    status?: 'OPEN' | 'ALL';
    limit?: number;
  }): Promise<CoinbaseEquityOrder[]> {
    const sp = new URLSearchParams();
    if (params?.status) sp.append('status', params.status);
    if (params?.limit) sp.append('limit', params.limit.toString());
    // Backend wraps the list: {orders, count, status, note}. `note` says
    // status=ALL has no history backing (client only lists OPEN orders).
    const res = await this.fetch<{ orders: CoinbaseEquityOrder[] } | CoinbaseEquityOrder[]>(
      `/api/coinbase/equities/orders?${sp}`
    );
    return Array.isArray(res) ? res : (res?.orders ?? []);
  }

  async getCoinbaseEquitiesProducts(params?: {
    q?: string;
    limit?: number;
  }): Promise<CoinbaseEquityProduct[]> {
    const sp = new URLSearchParams();
    if (params?.q) sp.append('q', params.q);
    if (params?.limit) sp.append('limit', params.limit.toString());
    return this.fetch(`/api/coinbase/equities/products?${sp}`);
  }

  async createAlpacaOrder(params: {
    symbol: string;
    side: 'buy' | 'sell';
    qty: number;
    order_type?: 'market' | 'limit';
    limit_price?: number;
  }, reauthToken?: string): Promise<{
    status: string;
    order_id: string;
    symbol: string;
    side: string;
    qty: number;
    type: string;
  }> {
    const searchParams = new URLSearchParams({
      symbol: params.symbol,
      side: params.side,
      qty: params.qty.toString(),
      order_type: params.order_type || 'market',
    });
    if (params.limit_price) searchParams.append('limit_price', params.limit_price.toString());

    const headers: Record<string, string> = {};
    if (reauthToken) headers['X-Reauth-Token'] = reauthToken;

    return this.fetch(`/api/alpaca/orders?${searchParams}`, {
      method: 'POST',
      headers,
    });
  }

  async cancelAlpacaOrder(orderId: string, reauthToken?: string): Promise<{ status: string; order_id: string }> {
    const headers: Record<string, string> = {};
    if (reauthToken) headers['X-Reauth-Token'] = reauthToken;

    return this.fetch(`/api/alpaca/orders/${orderId}`, {
      method: 'DELETE',
      headers,
    });
  }

  // ═══════════════════════════════════════════════════════════
  // OL_MAN ALPACA (Separate Alpaca Account)
  // ═══════════════════════════════════════════════════════════

  async getOlManAlpacaAccount(): Promise<{
    equity: number;
    cash: number;
    buying_power: number;
    portfolio_value: number;
    day_trade_count: number;
    pattern_day_trader: boolean;
    status: string;
  }> {
    return this.fetch('/api/alpaca-ol-man/account');
  }

  // ISS-090: shared types — see getAlpacaPositions above.
  async getOlManAlpacaPositions(): Promise<AlpacaPosition[]> {
    return this.fetch('/api/alpaca-ol-man/positions');
  }

  async getOlManAlpacaOrders(params?: { status?: string; limit?: number }): Promise<AlpacaOrder[]> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    return this.fetch(`/api/alpaca-ol-man/orders?${searchParams}`);
  }

  async getOlManAlpacaStats(): Promise<{
    equity: number;
    cash: number;
    starting_capital: number;
    total_pnl: number;
    return_pct: number;
    open_positions: number;
    total_trades: number;
    winning_trades: number;
    losing_trades: number;
    win_rate: number;
  }> {
    return this.fetch('/api/alpaca-ol-man/stats');
  }

  async createOlManAlpacaOrder(params: {
    symbol: string;
    side: 'buy' | 'sell';
    qty: number;
    order_type?: 'market' | 'limit';
    limit_price?: number;
  }, reauthToken?: string) {
    const searchParams = new URLSearchParams({
      symbol: params.symbol,
      side: params.side,
      qty: params.qty.toString(),
      order_type: params.order_type || 'market',
    });
    if (params.limit_price) searchParams.append('limit_price', params.limit_price.toString());
    const headers: Record<string, string> = {};
    if (reauthToken) headers['X-Reauth-Token'] = reauthToken;
    return this.fetch(`/api/alpaca-ol-man/orders?${searchParams}`, { method: 'POST', headers });
  }

  async cancelOlManAlpacaOrder(orderId: string, reauthToken?: string) {
    const headers: Record<string, string> = {};
    if (reauthToken) headers['X-Reauth-Token'] = reauthToken;
    return this.fetch(`/api/alpaca-ol-man/orders/${orderId}`, { method: 'DELETE', headers });
  }

  // ═══════════════════════════════════════════════════════════
  // CROSS-ASSET INTELLIGENCE + UNIVERSE SCANNER (2026-03-15)
  // ═══════════════════════════════════════════════════════════

  async getCrossAssetRegime(): Promise<{
    sentiment: string;
    size_multiplier: number;
    crypto_regime: string;
    equity_regime: string;
    flight_to_safety: boolean;
    confidence: number;
    reasoning: string;
    published_at: string;
    age_minutes: number;
    is_stale: boolean;
    error?: string;
  }> {
    return this.fetch('/api/cross-asset-regime');
  }

  async getUniverseScanner(): Promise<{
    generated_at: string;
    universe_size: number;
    scan_duration_seconds: number;
    age_minutes: number;
    is_stale: boolean;
    top_stocks: Array<{ symbol: string; rs_score: number; weighted_return?: number; returns_3m?: number }>;
    bottom_stocks: Array<{ symbol: string; rs_score: number }>;
    error?: string;
  }> {
    return this.fetch('/api/universe-scanner');
  }

  async getAcesStockStatus(): Promise<{
    last_run: string | null;
    tli_findings: Array<{ check: string; severity: string; message: string }>;
    strategy_tuner: Array<{ strategy: string; level: number; trades: number; pf: number; wr: number; status: string }>;
    stock_service_status: string;
    error?: string;
  }> {
    return this.fetch('/api/aces-stock-status');
  }

  async getMessageBusStatus(): Promise<{
    channels: Array<{
      channel: string;
      last_published: string | null;
      age_minutes: number | null;
      publisher: string | null;
      latest_value: string | null;
    }>;
    error?: string;
  }> {
    return this.fetch('/api/message-bus-status');
  }

  // ═══════════════════════════════════════════════════════════
  // STOCK TRADER (Isolated Stock Trading System)
  // ═══════════════════════════════════════════════════════════

  async getStockTraderStatus(): Promise<{
    service: {
      active: boolean;
      timer_enabled: boolean;
      status: 'running' | 'scheduled' | 'stopped';
    };
    account: {
      equity: number;
      cash: number;
      portfolio_value: number;
      buying_power: number;
    };
    positions_count: number;
    max_positions: number;
    drawdown: {
      ath: number;
      current_dd_pct: number;
      daily_pnl_pct: number;
      day_start_value: number;
      last_updated: string | null;
    };
    risk_state: {
      trading_halted: boolean;
      new_positions_blocked: boolean;
      size_reduced: boolean;
      size_multiplier: number;
      halt_reason: string | null;
    };
    config: {
      max_positions: number;
      risk_per_trade: number;
      min_risk_reward: number;
      scan_interval_minutes: number;
      drawdown_thresholds: {
        reduce: number;
        block: number;
        halt: number;
      };
      daily_loss_limit: number;
    };
  }> {
    return this.fetch('/api/stock-trader/status');
  }

  async getStockTraderSignals(params?: { state?: string; limit?: number }): Promise<{
    signals: Array<{
      id: number;
      symbol: string;
      side: string;
      strategy_name: string;
      state: string;
      created_at: string;
      updated_at: string;
      entry_price: number | null;
      stop_loss: number | null;
      take_profit: number | null;
      quantity: number | null;
      confidence: number | null;
      trade_id: string | null;
      exit_price: number | null;
      pnl: number | null;
      skip_reason: string | null;
      notes: string | null;
    }>;
    total: number;
    state_counts: Record<string, number>;
  }> {
    const searchParams = new URLSearchParams();
    if (params?.state) searchParams.append('state', params.state);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    return this.fetch(`/api/stock-trader/signals?${searchParams}`);
  }

  async getStockTraderConfig(): Promise<{
    position_limits: {
      max_positions: number;
      max_position_pct: number;
      max_portfolio_heat: number;
    };
    risk: {
      risk_per_trade: number;
      min_risk_reward: number;
      daily_loss_limit: number;
    };
    drawdown_thresholds: {
      reduce: number;
      halt_new: number;
      halt_all: number;
    };
    entry_filters: {
      min_price: number;
      max_price: number;
      min_avg_volume: number;
      min_confidence: number;
    };
    exit_params: {
      stop_loss_atr_mult: number;
      take_profit_atr_mult: number;
    };
    scan_settings: {
      interval_minutes: number;
      max_signals_per_scan: number;
      cooldown_minutes: number;
    };
  }> {
    return this.fetch('/api/stock-trader/config');
  }

  async getStockTraderPerformance(): Promise<{
    summary: {
      total_trades: number;
      wins: number;
      losses: number;
      win_rate_pct: number;
      total_pnl: number;
      avg_pnl: number;
      profit_factor: number;
      best_trade: { symbol: string; pnl: number } | null;
      worst_trade: { symbol: string; pnl: number } | null;
    };
    strategies: Record<string, {
      trades: number;
      wins: number;
      win_rate_pct: number;
      total_pnl: number;
      avg_pnl: number;
      status: 'active' | 'disabled';
    }>;
    mean_reversion_trades: Array<{
      symbol: string;
      pnl: number;
      confidence: number;
      entry_price: number;
      exit_price: number;
    }>;
    milestone: {
      target: number;
      current: number;
      remaining: number;
      progress_pct: number;
      estimated_days: number;
    };
    projections: {
      starting_capital: number;
      current_value: number;
      return_pct: number;
      monthly_rate_pct: number;
      year_1: number;
      year_2: number;
      year_3: number;
      year_5: number;
    };
  }> {
    return this.fetch('/api/stock-trader/performance');
  }

  async controlStockTrader(action: 'start' | 'stop' | 'enable-timer' | 'disable-timer', reauthToken?: string): Promise<{
    status: string;
    message: string;
  }> {
    const headers: Record<string, string> = {};
    if (reauthToken) headers['X-Reauth-Token'] = reauthToken;

    return this.fetch(`/api/stock-trader/control?action=${action}`, {
      method: 'POST',
      headers,
    });
  }

  // ═══════════════════════════════════════════════════════════
  // PHASE-AWARE SYSTEM (Accumulation + Early Move)
  // ═══════════════════════════════════════════════════════════

  async getPhaseAwareStatus(): Promise<{
    enabled: boolean;
    thresholds: {
      accumulation_ready: number;
      early_move_go: number;
    };
    ready_symbols: Array<{
      symbol: string;
      accumulation_score: number;
      early_move_score: number;
      phase_state: string;
      wyckoff_phase: string;
      is_go: boolean;
    }>;
    candidates: Array<{
      symbol: string;
      accumulation_score: number;
      early_move_score: number;
      wyckoff_phase: string;
      status: 'GO' | 'READY' | 'WATCHING';
      is_go: boolean;
    }>;
    dca_positions: Array<{
      symbol: string;
      invested_usd: number;
      quantity: number;
      avg_price: number;
      buy_count: number;
      last_score: number;
    }>;
    dca_summary: {
      total_invested: number;
      total_buys: number;
      weekly_pct: number;
      max_accumulation_pct: number;
    };
    config: Record<string, unknown>;
  }> {
    return this.fetch('/api/phase-aware');
  }

  // ═══════════════════════════════════════════════════════════
  // SETUP & PASSWORD AUTHENTICATION
  // ═══════════════════════════════════════════════════════════

  async getSetupStatus(): Promise<{
    initialized: boolean;
    needs_setup: boolean;
    user_count: number;
    auth_module_available: boolean;
  }> {
    return this.fetch('/api/auth/setup/status');
  }

  async initializeSetup(username: string, password: string, displayName?: string): Promise<AuthResponse & { next_step: string }> {
    return this.fetch('/api/auth/setup/init', {
      method: 'POST',
      body: JSON.stringify({
        username,
        password,
        display_name: displayName
      }),
    });
  }

  async loginWithPassword(username: string, password: string): Promise<AuthResponse & { next_step: string | null }> {
    return this.fetch('/api/auth/login/password', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  }

  // ═══════════════════════════════════════════════════════════
  // AUTHENTICATION - WebAuthn Passkey
  // ═══════════════════════════════════════════════════════════

  // Simplified methods for setup wizard
  async getPasskeyRegistrationOptions(username: string, displayName?: string): Promise<PublicKeyCredentialCreationOptionsJSON> {
    return this.fetch('/api/auth/register/options', {
      method: 'POST',
      body: JSON.stringify({ username, display_name: displayName || username }),
    });
  }

  async verifyPasskeyRegistration(
    challengeId: string,
    credential: {
      id: string;
      rawId: string;
      type: string;
      response: {
        clientDataJSON: string;
        attestationObject: string;
        transports?: string[];
      };
    },
    deviceName?: string
  ): Promise<AuthResponse> {
    return this.fetch('/api/auth/register/verify', {
      method: 'POST',
      body: JSON.stringify({
        challenge_id: challengeId,
        credential,
        device_name: deviceName,
      }),
    });
  }

  async getRegisterOptions(username: string, displayName?: string): Promise<PublicKeyCredentialCreationOptionsJSON> {
    return this.fetch('/api/auth/register/options', {
      method: 'POST',
      body: JSON.stringify({ username, display_name: displayName || username }),
    });
  }

  async verifyRegistration(
    challengeId: string,
    credential: PublicKeyCredential,
    deviceName?: string
  ): Promise<AuthResponse> {
    const response = credential.response as AuthenticatorAttestationResponse;

    return this.fetch('/api/auth/register/verify', {
      method: 'POST',
      body: JSON.stringify({
        challenge_id: challengeId,
        credential: {
          id: credential.id,
          rawId: this.bufferToBase64url(credential.rawId),
          type: credential.type,
          response: {
            clientDataJSON: this.bufferToBase64url(response.clientDataJSON),
            attestationObject: this.bufferToBase64url(response.attestationObject),
            transports: response.getTransports?.() || [],
          },
          authenticatorAttachment: (credential as any).authenticatorAttachment,
        },
        device_name: deviceName,
      }),
    });
  }

  async getLoginOptions(username?: string): Promise<PublicKeyCredentialRequestOptionsJSON> {
    const url = username ? `/api/auth/login/options?username=${encodeURIComponent(username)}` : '/api/auth/login/options';
    return this.fetch(url, { method: 'POST' });
  }

  async verifyLogin(challengeId: string, credential: PublicKeyCredential): Promise<AuthResponse> {
    const response = credential.response as AuthenticatorAssertionResponse;

    return this.fetch('/api/auth/login/verify', {
      method: 'POST',
      body: JSON.stringify({
        challenge_id: challengeId,
        credential: {
          id: credential.id,
          rawId: this.bufferToBase64url(credential.rawId),
          type: credential.type,
          response: {
            clientDataJSON: this.bufferToBase64url(response.clientDataJSON),
            authenticatorData: this.bufferToBase64url(response.authenticatorData),
            signature: this.bufferToBase64url(response.signature),
            userHandle: response.userHandle ? this.bufferToBase64url(response.userHandle) : null,
          },
        },
      }),
    });
  }

  async refreshToken(refreshToken: string): Promise<{ access_token: string; refresh_token: string }> {
    return this.fetch(`/api/auth/refresh?refresh_token=${encodeURIComponent(refreshToken)}`, {
      method: 'POST',
    });
  }

  async logout(): Promise<{ status: string }> {
    return this.fetch('/api/auth/logout', { method: 'POST' });
  }

  async getCurrentUser(): Promise<{
    authenticated: boolean;
    dev_mode?: boolean;
    auth_type?: string;
    user?: { id: string; username: string; display_name: string; is_admin: boolean };
  }> {
    return this.fetch('/api/auth/me');
  }

  // Passkey Management
  async listPasskeys(): Promise<Passkey[]> {
    return this.fetch('/api/auth/passkeys');
  }

  async deletePasskey(credentialId: string): Promise<{ status: string }> {
    return this.fetch(`/api/auth/passkeys/${encodeURIComponent(credentialId)}`, {
      method: 'DELETE',
    });
  }

  // Re-authentication for sensitive actions
  async getReauthOptions(): Promise<PublicKeyCredentialRequestOptionsJSON> {
    return this.fetch('/api/auth/reauth/options', { method: 'POST' });
  }

  async verifyReauth(challengeId: string, credential: PublicKeyCredential): Promise<{
    verified: boolean;
    reauth_token: string;
    expires_in: number;
  }> {
    const response = credential.response as AuthenticatorAssertionResponse;

    return this.fetch('/api/auth/reauth/verify', {
      method: 'POST',
      body: JSON.stringify({
        challenge_id: challengeId,
        credential: {
          id: credential.id,
          rawId: this.bufferToBase64url(credential.rawId),
          type: credential.type,
          response: {
            clientDataJSON: this.bufferToBase64url(response.clientDataJSON),
            authenticatorData: this.bufferToBase64url(response.authenticatorData),
            signature: this.bufferToBase64url(response.signature),
            userHandle: response.userHandle ? this.bufferToBase64url(response.userHandle) : null,
          },
        },
      }),
    });
  }

  // Recovery Codes
  async generateRecoveryCodes(): Promise<{ codes: string[]; count: number; warning: string }> {
    return this.fetch('/api/auth/recovery/generate', { method: 'POST' });
  }

  async verifyRecoveryCode(username: string, code: string): Promise<{
    status: string;
    tokens: { access_token: string; refresh_token: string };
    recovery_codes_remaining: number;
  }> {
    return this.fetch('/api/auth/recovery/verify', {
      method: 'POST',
      body: JSON.stringify({ username, code }),
    });
  }

  async getRecoveryCodeCount(): Promise<{ count: number }> {
    return this.fetch('/api/auth/recovery/count');
  }

  // ═══════════════════════════════════════════════════════════
  // SYSTEM LOGS
  // ═══════════════════════════════════════════════════════════

  async getLogs(lines: number = 50, since: string = '30 minutes ago'): Promise<{
    services: Record<string, string>;
    since: string;
    total_logs: number;
    logs: Array<{
      timestamp: string;
      service: string;
      level: string;
      message: string;
    }>;
  }> {
    return this.fetch(`/api/logs?lines=${lines}&since=${encodeURIComponent(since)}`);
  }

  async getServiceLogs(service: string, lines: number = 100, since: string = '1 hour ago'): Promise<{
    service: string;
    status: string;
    since: string;
    lines_requested: number;
    lines_returned: number;
    logs: Array<{
      timestamp: string;
      level: string;
      message: string;
    }>;
  }> {
    return this.fetch(`/api/logs/${service}?lines=${lines}&since=${encodeURIComponent(since)}`);
  }

  // ═══════════════════════════════════════════════════════════
  // Proving ground + benchmark (capital-preservation mandate surfaces)
  // ═══════════════════════════════════════════════════════════

  async getShadowSummary(): Promise<{
    cycle: {
      inception: string; days_logged: number; rebalances: number;
      latest: { date: string; price: number; dd: number; target: number; action: string; equity: number; hodl_eq: number; usdc_eq: number };
      series: Array<{ date: string; equity: number; hodl_eq: number; usdc_eq: number }>;
    } | null;
    strategies: {
      occupants: Array<{ occupant: string; n: number; pnl: number; wins: number; last_exit: string }>;
      open_trades: number;
    } | null;
  }> {
    return this.fetch('/api/shadow/summary');
  }

  async getBenchmark(): Promise<Record<string, unknown>> {
    return this.fetch('/api/benchmark/bot-vs-btc');
  }

  // ═══════════════════════════════════════════════════════════
  // Reports (operator briefs/digests mirrored from Telegram)
  // ═══════════════════════════════════════════════════════════

  async getReports(kind?: string): Promise<{
    reports: Array<{ id: number; ts_utc: string; kind: string; title: string; body: string }>;
    kinds: string[];
  }> {
    const q = kind ? `?kind=${encodeURIComponent(kind)}` : '';
    return this.fetch(`/api/reports${q}`);
  }

  // ═══════════════════════════════════════════════════════════
  // A.C.E.S. (Autonomous Cognitive Execution System)
  // ═══════════════════════════════════════════════════════════

  async getAcesStatus(): Promise<AcesStatus> {
    return this.fetch('/api/aces/status');
  }

  async getAcesFindings(): Promise<{ findings: AcesFinding[]; total: number }> {
    return this.fetch('/api/aces/findings');
  }

  async getAcesFixes(): Promise<{ fixes: AcesFix[]; total: number }> {
    return this.fetch('/api/aces/fixes');
  }

  async getAcesBudget(): Promise<AcesBudget> {
    return this.fetch('/api/aces/budget');
  }

  async getAcesAnomalies(): Promise<{ anomalies: AcesAnomaly[] }> {
    return this.fetch('/api/aces/anomalies');
  }

  // ARIA Research Engine
  async getAriaStatus(): Promise<AriaStatus> {
    return this.fetch('/api/aria-status');
  }

  async getStrategyTuner(): Promise<StrategyTunerReport> {
    return this.fetch('/api/strategy-tuner');
  }

  // Generic get method for custom endpoints
  async get<T>(endpoint: string): Promise<T> {
    return this.fetch(endpoint);
  }

  // ═══════════════════════════════════════════════════════════
  // WEBAUTHN HELPERS
  // ═══════════════════════════════════════════════════════════

  private bufferToBase64url(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  base64urlToBuffer(base64url: string): ArrayBuffer {
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - base64.length % 4) % 4);
    const binary = atob(base64 + padding);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  // Convert server options to WebAuthn API format
  prepareRegistrationOptions(options: PublicKeyCredentialCreationOptionsJSON): PublicKeyCredentialCreationOptions {
    return {
      rp: options.rp,
      user: {
        ...options.user,
        id: this.base64urlToBuffer(options.user.id),
      },
      challenge: this.base64urlToBuffer(options.challenge),
      pubKeyCredParams: options.pubKeyCredParams.map(param => ({
        ...param,
        type: 'public-key' as const,
      })),
      timeout: options.timeout,
      excludeCredentials: options.excludeCredentials?.map(cred => ({
        id: this.base64urlToBuffer(cred.id),
        type: 'public-key' as const,
        transports: cred.transports as AuthenticatorTransport[],
      })),
      authenticatorSelection: options.authenticatorSelection ? {
        authenticatorAttachment: options.authenticatorSelection.authenticatorAttachment as AuthenticatorAttachment | undefined,
        residentKey: options.authenticatorSelection.residentKey as ResidentKeyRequirement | undefined,
        requireResidentKey: options.authenticatorSelection.requireResidentKey,
        userVerification: options.authenticatorSelection.userVerification as UserVerificationRequirement | undefined,
      } : undefined,
      attestation: options.attestation as AttestationConveyancePreference | undefined,
    };
  }

  prepareAuthenticationOptions(options: PublicKeyCredentialRequestOptionsJSON): PublicKeyCredentialRequestOptions {
    // IMPORTANT: Safari hangs/spins forever when allowCredentials is an empty array [].
    // py_webauthn serializes allow_credentials=None to "allowCredentials": [] in JSON.
    // An empty array tells Safari "specific credentials required, but none exist" causing
    // it to wait indefinitely. Setting to undefined triggers discoverable credential mode
    // which properly prompts Face ID / Touch ID / passkey selection.
    const creds = options.allowCredentials?.length
      ? options.allowCredentials.map(cred => ({
          id: this.base64urlToBuffer(cred.id),
          type: 'public-key' as const,
          transports: cred.transports as AuthenticatorTransport[],
        }))
      : undefined;

    return {
      challenge: this.base64urlToBuffer(options.challenge),
      timeout: options.timeout,
      rpId: options.rpId,
      allowCredentials: creds,
      userVerification: options.userVerification as UserVerificationRequirement | undefined,
    };
  }
  // Generic POST for Governor approval and other new modules
  async post<T = any>(endpoint: string, body?: any): Promise<T> {
    return this.fetch<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }
}

export const api = new ApiService();
